import { useCallback, useReducer, useRef } from 'react';
import _ from 'lodash';
import { createAssessmentRequest, workflowIdFromResponse } from '../../assessment-portal/services';
import analytics from '../../infra/analytics';
import { AppointmentType, RequestStatus } from '../../model';
import {
  ASSESSMENT_TYPE, EVENT_NAMES,
} from '../data/constants';
import { buildCarePathwayMetadata, buildConsentMetadata } from '../utils/build-consent-metadata';
import { clearUtm, readUtm, resolveSource } from '../utils/source-tracking';
import { saveBookingConfirmationSnapshot } from '../utils/booking-confirmation-snapshot';
import { clearDraftWorkflow, resolveDraftWorkflowId } from '../utils/draft-workflow';
import { trackConfirmError, trackConfirmSuccess } from '../utils/booking-analytics';
import type { BookingTiming } from '../utils/booking-analytics';
import type { AdhdFlowState, BookingValues } from '../types/flow';
import type { AssessmentRequestPayload } from '../../assessment-portal/types/questionnaire';
import type { SubmissionFailure } from './submission-state';
import {
  FEE_ESTIMATE_MISSING_MESSAGE,
  initialSubmissionState,
  submissionReducer,
  toFailure,
} from './submission-state';

/** The draft was booked by an earlier confirm whose response never arrived. */
const ALREADY_CONFIRMED_ERROR_CODE = 'ALREADY_CONFIRMED';

/** Only the total the patient was shown; the server re-prices and stores the rest. */
interface FeeEstimate {
  gross: number
}

const useBookingSubmission = (
  flowState: AdhdFlowState,
  feeEstimate: FeeEstimate | null,
) => {
  const [state, dispatch] = useReducer(submissionReducer, initialSubmissionState);
  // Set before any re-render, so exit tracking sees the success even when the
  // step closes in the same tick.
  const confirmedRef = useRef(false);
  // Read by the booking page for the confirm click; it reports picks through markSlotSelected.
  const timingRef = useRef<BookingTiming>({ enteredAt: Date.now(), slotSelectedAt: null });
  const markSlotSelected = useCallback(() => {
    timingRef.current = { ...timingRef.current, slotSelectedAt: Date.now() };
  }, []);

  const handleSubmitBooking = async (
    bookingValues: BookingValues,
    onSuccess: () => void,
  ) => {
    dispatch({ type: 'SUBMIT_STARTED' });

    const confirm = {
      clinicianName: bookingValues.clinicianName || '',
      startDateTime: bookingValues.startDateTime,
      timing: timingRef.current,
    };

    if (!feeEstimate) {
      const failure: SubmissionFailure = { kind: 'validation', message: FEE_ESTIMATE_MISSING_MESSAGE };
      trackConfirmError(confirm, failure);
      dispatch({ type: 'SUBMIT_FAILED', failure });
      return;
    }

    const {
      patientInfo, medicalHistory, fee,
    } = flowState;
    const draftWorkflowId = await resolveDraftWorkflowId();

    const addressObj = {
      streetAddress: patientInfo.address,
      suburb: patientInfo.suburb,
      state: patientInfo.state,
      postcode: patientInfo.postcode,
    };

    const buildMedicareCard = (): AssessmentRequestPayload['patientInfo']['medicareCard'] | undefined => {
      if (patientInfo.medicareType === 'none' || patientInfo.medicareType === '') {
        return undefined;
      }
      if (patientInfo.medicareType === 'medicare') {
        return {
          medicareCardNumber: patientInfo.medicareNumber,
          medicareCardIRN: patientInfo.medicareRef,
          expiry: patientInfo.medicareExpiry,
        };
      }
      if (patientInfo.medicareType === 'dva') {
        const cardType = patientInfo.dvaCardType as 'Gold' | 'Orange' | 'White' | '';
        return {
          dva: {
            cardNumber: patientInfo.dvaNumber,
            ...(cardType ? { cardType } : {}),
          },
        };
      }
      return undefined;
    };

    const medicareCard = buildMedicareCard();
    const attachments = _.compact([
      medicalHistory.prevReportFile,
      ...medicalHistory.docs,
    ]);

    const utm = readUtm();
    const source = resolveSource(utm);

    const payload: AssessmentRequestPayload = {
      assessmentType: ASSESSMENT_TYPE,
      ...(draftWorkflowId ? { workflowId: draftWorkflowId } : {}),
      patientInfo: {
        firstName: patientInfo.firstName,
        lastName: patientInfo.lastName,
        dateOfBirth: patientInfo.dateOfBirth,
        gender: patientInfo.gender,
        mobile: patientInfo.mobile,
        email: patientInfo.email,
        addressResidential: addressObj,
        addressPostal: addressObj,
        ...(medicareCard ? { medicareCard } : {}),
        nextOfKin: {
          name: patientInfo.emergencyName,
          relationshipToYou: patientInfo.emergencyRelationship,
          mobile: patientInfo.emergencyMobile,
        },
      },
      appointmentInfo: {
        startDateTime: bookingValues.startDateTime,
        endDateTime: bookingValues.endDateTime,
        appointmentType: AppointmentType.NEW,
        specialistId: bookingValues.clinicianId,
        timezone: bookingValues.timezone,
      },
      metadata: {
        cultural: patientInfo.cultural,
        referral: patientInfo.referral,
        clinicianName: bookingValues.clinicianName || '',
        clinicianTitle: bookingValues.clinicianTitle || '',
        fee: {
          location: fee.location,
          feeAssessmentScope: fee.feeAssessmentScope,
          prescriptionType: fee.prescriptionType,
          seenPsychiatrist: fee.seenPsychiatrist,
          gross: feeEstimate.gross,
        },
        medicalHistory: {
          occupation: medicalHistory.occupation,
          marital: medicalHistory.marital,
          children: medicalHistory.children,
          living: medicalHistory.living,
          seenSpecialist: medicalHistory.seenSpecialist,
          seenPsychiatrist12m: medicalHistory.seenPsychiatrist12m,
          psychiatristDetail: medicalHistory.psychiatristDetail,
          reasonForConsult: medicalHistory.reasonForConsult,
          reasonOther: medicalHistory.reasonOther,
          declinedDiagnosis: medicalHistory.declinedDiagnosis,
          prevDiagnosis: medicalHistory.prevDiagnosis,
          otherDiagnosis: medicalHistory.otherDiagnosis,
          consultReasonNotReferral: medicalHistory.consultReasonNotReferral,
          consultReasonNotReferralOther: medicalHistory.consultReasonNotReferralOther,
          familyHistory: medicalHistory.familyHistory,
          physicalConditions: medicalHistory.physicalConditions,
          medications: medicalHistory.medications,
          substances: medicalHistory.substances,
        },
        consents: buildConsentMetadata(flowState),
        carePathway: buildCarePathwayMetadata(flowState),
        source,
        ...(utm ? { utm } : {}),
        ...(attachments.length > 0 ? { questionnaireAttachments: attachments } : {}),
      },
    };

    const result = await createAssessmentRequest(payload);
    const alreadyConfirmed = result.status === RequestStatus.FAILURE
      && result.errorCode === ALREADY_CONFIRMED_ERROR_CODE
      && Boolean(draftWorkflowId);

    if (result.status === RequestStatus.SUCCESS || alreadyConfirmed) {
      const bookedWorkflowId = (result.status === RequestStatus.SUCCESS
        && workflowIdFromResponse(result.data)) || draftWorkflowId;
      confirmedRef.current = true;
      trackConfirmSuccess(confirm);
      analytics.logEvent(EVENT_NAMES.REFERRAL_SOURCE_SELECTED, {
        answer: payload.metadata?.referral,
      });

      saveBookingConfirmationSnapshot({
        version: 1,
        firstName: patientInfo.firstName,
        lastName: patientInfo.lastName,
        dateOfBirth: patientInfo.dateOfBirth,
        email: patientInfo.email,
        clinicianName: bookingValues.clinicianName || '',
        clinicianTitle: bookingValues.clinicianTitle || '',
        startDateTime: bookingValues.startDateTime,
        ...(bookedWorkflowId ? { workflowId: bookedWorkflowId } : {}),
      });
      clearDraftWorkflow();

      dispatch({ type: 'SUBMIT_SUCCEEDED' });
      onSuccess();
      clearUtm();
      return;
    }

    const errorMsg = result.error || 'Submit booking failed';
    const failure = toFailure(result.errorCode, errorMsg);
    trackConfirmError(confirm, failure, { httpStatus: result.httpStatus, networkError: result.networkError });
    dispatch({ type: 'SUBMIT_FAILED', failure });
  };

  const resetSubmitError = () => {
    dispatch({ type: 'RESET_ERROR' });
  };

  return {
    state,
    confirmedRef,
    timingRef,
    markSlotSelected,
    handleSubmitBooking,
    resetSubmitError,
  };
};

export default useBookingSubmission;
